import useLocalisedCountryName from '@app/src/hooks/useLocalisedCountryName'
import { useProviderEnrichedValues } from '@app/src/hooks/useProviderEnrichedValues'
import type { GenericOrganization, Provider } from '@app/src/types/organizations'
import { Business, LanguageOutlined, NumbersOutlined } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import React from 'react'
import ReactCountryFlag from 'react-country-flag'

interface OrganizationDetailsGlanceCardProps {
  company?: GenericOrganization | Provider
  visibleFields?: Array<'country' | 'websiteAddress' | 'vatOrRegistrationNumber' | 'industry'>
}

export const OrganizationDetailsGlanceCard: React.FC<OrganizationDetailsGlanceCardProps> = ({
  company,
  visibleFields = ['country', 'websiteAddress', 'vatOrRegistrationNumber', 'industry'],
}) => {
  const { getLocalisedCountryName } = useLocalisedCountryName()
  // Get enriched values if this is a Provider (must be called before any early return)
  const enrichedValues = useProviderEnrichedValues(company as Provider)

  if (!company) return null

  const country = company.organization?.country || company?.country
  return (
    <Stack gap={0.5}>
      {visibleFields.includes('industry') && enrichedValues.industry.value && (
        <Box display="flex" alignItems="center" gap={1}>
          <Business color="action" fontSize="small" />
          <Typography variant="caption" color="text.secondary">
            {enrichedValues.industry.value}
          </Typography>
        </Box>
      )}
      {visibleFields.includes('country') && country?.name && (
        <Box display="flex" alignItems="center" gap={1}>
          <Stack width="20px" height="20px" alignItems="center" justifyContent="center" flexShrink="0">
            <ReactCountryFlag countryCode={country.countryCode} svg style={{ fontSize: '1.3em' }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {getLocalisedCountryName(country.countryCode)}
          </Typography>
        </Box>
      )}
      {visibleFields.includes('websiteAddress') && enrichedValues.websiteAddress.value && (
        <Box display="flex" alignItems="center" gap={1}>
          <LanguageOutlined color="action" fontSize="small" />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              wordBreak: 'break-word',
            }}
          >
            {enrichedValues.websiteAddress.value}
          </Typography>
        </Box>
      )}
      {visibleFields.includes('vatOrRegistrationNumber') &&
        (enrichedValues.vatNumber.value || enrichedValues.registrationNumber.value) && (
          <Box display="flex" alignItems="center" gap={1}>
            <NumbersOutlined color="action" fontSize="small" />
            <Typography variant="caption" color="text.secondary">
              {[enrichedValues.vatNumber.value, enrichedValues.registrationNumber.value].filter(Boolean).join(' / ')}
            </Typography>
          </Box>
        )}
    </Stack>
  )
}
